import React, { useEffect, useMemo, useState } from "react";
import { client } from "@/lib/api";
import { toast } from "sonner";
import { confirm } from "@/lib/dialog";
import { useDeleteWindow } from "@/lib/deleteWindow";

/**
 * Wave 6 · admin venue editor (Vania · 30 Sep).
 * - Search by name, sort by name / event count.
 * - "duplicate groups" panel groups likely dupes (same/similar name + same area or pin).
 * - Merge picker moves events / reviews / photos / follows / ratings into a keeper
 *   and logs the merge (backend `/admin/venues/merge-log`).
 * - Full editor: name / area / town / address / map pin / socials / photo /
 *   outside_nairobi flag / directions (off-map venues).
 * D21 · venue rating remains a SEPARATE instrument · no code path from
 * rating → event score. Merged venue keeps its rating rows via venue_id
 * remap.
 */
const emptyForm = {
  name: "", aliases: "", area: "", town: "", address: "",
  directions: "", instagram_url: "", website: "", photo_url: "",
  outside_nairobi: false, google_maps_url: "",
};

export default function VenuesPage() {
  const [rows, setRows] = useState([]);
  const deleteOpen = useDeleteWindow();
  const [del, setDel] = useState(null); // WW-190 · {venue, counts, moveTo}
  const [groups, setGroups] = useState([]);
  const [q, setQ] = useState("");
  const [sortKey, setSortKey] = useState("name"); // "name" | "events_desc" | "events_asc"
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [mergeState, setMergeState] = useState(null); // { keep_id, ids: [] }

  const load = async () => {
    setLoading(true);
    try {
      const [rv, rg] = await Promise.all([
        client.get("/admin/venues"),
        client.get("/admin/venues/duplicate-groups").catch(() => ({ data: { groups: [] } })),
      ]);
      setRows(rv.data.venues || []);
      setGroups(rg.data.groups || []);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!form.name.trim()) return toast.error("name is required");
    const payload = {
      name: form.name.trim(),
      aliases: form.aliases.split(",").map((s) => s.trim()).filter(Boolean),
      area: form.area.trim(),
      address: form.address.trim(),
      directions: form.directions.trim(),
      google_maps_url: form.google_maps_url.trim(),
    };
    const editPayload = {
      ...payload,
      town: form.town.trim(),
      instagram_url: form.instagram_url.trim(),
      website: form.website.trim(),
      photo_url: form.photo_url.trim(),
      outside_nairobi: !!form.outside_nairobi,
      on_maps: !!(form.google_maps_url && form.google_maps_url.trim()),
    };
    try {
      if (editingId) {
        // Use upsert (which merges) then PUT for the fields upsert doesn't touch.
        await client.put(`/admin/venues/${editingId}`, editPayload);
      } else {
        await client.post("/admin/venues/upsert", payload);
      }
      setForm(emptyForm); setEditingId(null);
      toast.success("saved"); await load();
    } catch { toast.error("save failed"); }
  };
  const startEdit = (v) => {
    setEditingId(v.id);
    setForm({
      name: v.name || "", aliases: (v.aliases || []).join(", "),
      area: v.area || "", town: v.town || "", address: v.address || "",
      directions: v.directions || "", instagram_url: v.instagram_url || "",
      website: v.website || "", photo_url: v.photo_url || "",
      outside_nairobi: !!v.outside_nairobi, google_maps_url: v.google_maps_url || "",
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };
  const remove = async (id) => {
    const venue = rows.find((x) => x.id === id);
    let counts = { published: 0, pending: 0, total: 0 };
    try { counts = (await client.get(`/admin/venues/${id}/event-counts`)).data; } catch { /* server re-checks */ }
    setDel({ venue, counts, moveTo: "" });
  };
  const confirmRemove = async () => {
    if (!del) return;
    if (del.counts.total > 0 && !del.moveTo) { toast.error("pick the venue its events move to"); return; }
    try {
      const q = del.counts.total > 0 ? `?move_to=${encodeURIComponent(del.moveTo)}` : "";
      await client.delete(`/admin/venues/${del.venue.id}${q}`);
      toast.success(del.counts.total > 0 ? "events moved · venue deleted" : "deleted");
      setDel(null); await load();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(detail?.message || detail?.hint || "delete failed");
    }
  };

  const filtered = useMemo(() => {
    let out = (rows || []).filter((v) => {
      if (!q.trim()) return true;
      const ql = q.trim().toLowerCase();
      return (v.name || "").toLowerCase().includes(ql)
        || (v.area || "").toLowerCase().includes(ql)
        || (v.aliases || []).some((a) => a.toLowerCase().includes(ql));
    });
    const totalEvents = (v) => (v.event_count_upcoming || 0) + (v.event_count_past || 0);
    if (sortKey === "name") out = [...out].sort((a, b) => (a.name || "").localeCompare(b.name || ""));
    if (sortKey === "events_desc") out = [...out].sort((a, b) => totalEvents(b) - totalEvents(a));
    if (sortKey === "events_asc") out = [...out].sort((a, b) => totalEvents(a) - totalEvents(b));
    return out;
  }, [rows, q, sortKey]);

  const openMerge = (grp) => setMergeState({
    keep_id: grp[0]?.id || "",
    ids: grp.map((v) => v.id),
    rows: grp,
  });
  const closeMerge = () => setMergeState(null);
  const runMerge = async () => {
    if (!mergeState?.keep_id) return;
    const losers = mergeState.ids.filter((i) => i !== mergeState.keep_id);
    if (!losers.length) return toast.error("pick a different keeper");
    try {
      const r = await client.post("/admin/venues/merge",
        { keep_id: mergeState.keep_id, merge_ids: losers });
      toast.success(`merged ${losers.length} · moved ${JSON.stringify(r.data.moved)}`);
      closeMerge(); await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "merge failed"); }
  };

  return (
    <div className="p-6" data-testid="admin-venues">
      <p className="text-[11px] uppercase tracking-[0.22em] text-neutral-500">watu wako · admin</p>
      <h1 className="mt-1 lowercase" style={{ fontFamily: "Poppins", fontWeight: 800, letterSpacing: "-0.025em", fontSize: 30 }}>
        venues
      </h1>
      <p className="mt-1 text-sm text-neutral-600">
        each real venue should exist exactly once. use merge (never delete) when a duplicate is found.
      </p>

      {/* Duplicate groups */}
      {groups.length > 0 && (
        <section data-testid="venue-dup-groups"
                 className="mt-6 rounded-lg border border-amber-300 bg-amber-50 p-4">
          <p className="text-[11px] uppercase tracking-[0.22em] text-amber-800">
            {groups.length} likely duplicate group{groups.length === 1 ? "" : "s"} · {groups.reduce((n, g) => n + g.length, 0)} venues
          </p>
          <div className="mt-3 grid gap-3">
            {groups.map((grp, gi) => (
              <div key={gi} data-testid={`venue-dup-group-${gi}`}
                   className="rounded-md border border-amber-200 bg-white p-3">
                <div className="flex flex-wrap items-center gap-2">
                  {grp.map((v) => (
                    <span key={v.id} data-testid={`dup-chip-${v.id}`}
                          className="rounded-full border border-neutral-300 bg-neutral-50 px-2 py-0.5 text-xs">
                      {v.name} · {(v.event_count_upcoming || 0) + (v.event_count_past || 0)} events{v.area ? ` · ${v.area}` : ""}
                    </span>
                  ))}
                  <button onClick={() => openMerge(grp)} data-testid={`dup-merge-open-${gi}`}
                          className="ml-auto rounded-md bg-amber-600 px-3 py-1 text-xs font-semibold text-white">
                    open merge
                  </button>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      <div className="mt-6 rounded-lg border border-neutral-200 bg-white p-5">
        <p className="mb-3 text-[11px] uppercase tracking-[0.14em] text-neutral-500">{editingId ? "edit venue" : "add venue"}</p>
        <div className="grid grid-cols-1 gap-3 md:grid-cols-4">
          <input className="rounded-md border border-neutral-300 p-2 text-sm" placeholder="name"
                 value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                 data-testid="venue-form-name" />
          <input className="rounded-md border border-neutral-300 p-2 text-sm" placeholder="area (e.g. westlands)"
                 value={form.area} onChange={(e) => setForm({ ...form, area: e.target.value })}
                 data-testid="venue-form-area" />
          <input className="rounded-md border border-neutral-300 p-2 text-sm" placeholder="town (e.g. naivasha, non-nairobi)"
                 value={form.town} onChange={(e) => setForm({ ...form, town: e.target.value })}
                 data-testid="venue-form-town" />
          <input className="rounded-md border border-neutral-300 p-2 text-sm" placeholder="address (optional)"
                 value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })}
                 data-testid="venue-form-address" />
          <input className="rounded-md border border-neutral-300 p-2 text-sm md:col-span-2"
                 placeholder="google maps link (used for the open in google maps button)"
                 value={form.google_maps_url} onChange={(e) => setForm({ ...form, google_maps_url: e.target.value })}
                 data-testid="venue-form-mapsurl" />
          <input className="rounded-md border border-neutral-300 p-2 text-sm md:col-span-2"
                 placeholder="text directions (for off-map venues)"
                 value={form.directions} onChange={(e) => setForm({ ...form, directions: e.target.value })}
                 data-testid="venue-form-directions" />
          <input className="rounded-md border border-neutral-300 p-2 text-sm" placeholder="instagram url"
                 value={form.instagram_url} onChange={(e) => setForm({ ...form, instagram_url: e.target.value })}
                 data-testid="venue-form-ig" />
          <input className="rounded-md border border-neutral-300 p-2 text-sm" placeholder="website"
                 value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })}
                 data-testid="venue-form-website" />
          <input className="rounded-md border border-neutral-300 p-2 text-sm" placeholder="photo url"
                 value={form.photo_url} onChange={(e) => setForm({ ...form, photo_url: e.target.value })}
                 data-testid="venue-form-photo" />
          <input className="rounded-md border border-neutral-300 p-2 text-sm" placeholder="aliases (comma separated)"
                 value={form.aliases} onChange={(e) => setForm({ ...form, aliases: e.target.value })}
                 data-testid="venue-form-aliases" />
          <label className="flex items-center gap-2 text-sm text-neutral-700 md:col-span-2">
            <input type="checkbox" checked={form.outside_nairobi}
                   onChange={(e) => setForm({ ...form, outside_nairobi: e.target.checked })}
                   data-testid="venue-form-outside-nairobi" />
            outside nairobi (feeds the outside-nairobi section)
          </label>
        </div>
        <div className="mt-3 flex gap-2">
          <button onClick={save} data-testid="venue-save"
                  className="rounded-md px-4 py-2 text-sm font-semibold"
                  style={{ background: "#FFD60A", color: "#1A1A30" }}>
            {editingId ? "update" : "add"}
          </button>
          {editingId && (
            <button onClick={() => { setEditingId(null); setForm(emptyForm); }}
                    className="text-sm text-neutral-500 underline" data-testid="venue-form-cancel">cancel edit</button>
          )}
        </div>
      </div>

      <div className="mt-6 flex flex-wrap items-center gap-3">
        <input placeholder="filter by name, area or alias" value={q} onChange={(e) => setQ(e.target.value)}
               data-testid="venues-search"
               className="w-full max-w-md rounded-md border border-neutral-300 p-2 text-sm" />
        <select value={sortKey} onChange={(e) => setSortKey(e.target.value)}
                data-testid="venues-sort"
                className="rounded-md border border-neutral-300 p-2 text-sm">
          <option value="name">sort · name</option>
          <option value="events_desc">sort · most events</option>
          <option value="events_asc">sort · fewest events</option>
        </select>
        <p className="text-xs text-neutral-500">{filtered.length} of {rows.length}</p>
      </div>

      <div className="mt-4 overflow-x-auto rounded-lg border border-neutral-200 bg-white">
        <table className="w-full text-sm min-w-[720px]">
          <thead className="bg-neutral-50 text-left">
            <tr>
              <th className="p-3">name</th><th className="p-3">area</th>
              <th className="p-3">upcoming</th><th className="p-3">past</th>
              <th className="p-3">flags</th>
              <th className="p-3"></th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={6} className="p-8 text-center text-neutral-500">loading…</td></tr>}
            {!loading && filtered.map((v) => (
              <tr key={v.id} className="border-t border-neutral-100" data-testid={`venue-row-${v.id}`}>
                <td className="p-3 font-semibold lowercase">
                  {v.name}
                  {(v.aliases || []).length > 0 && (
                    <span className="ml-2 text-[10px] text-neutral-500">· {v.aliases.length} alias</span>
                  )}
                </td>
                <td className="p-3 text-neutral-600">{(v.area || "").toLowerCase() || "—"}</td>
                <td className="p-3 text-neutral-800">{v.event_count_upcoming ?? 0}</td>
                <td className="p-3 text-neutral-500">{v.event_count_past ?? 0}</td>
                <td className="p-3 text-neutral-500 text-[11px]">
                  {v.outside_nairobi && <span data-testid={`flag-outside-${v.id}`} className="mr-2">outside nairobi</span>}
                  {!v.on_maps && <span data-testid={`flag-offmap-${v.id}`}>off map</span>}
                </td>
                <td className="p-3 text-right">
                  <button className="mr-3 text-xs underline" onClick={() => startEdit(v)}
                          data-testid={`venue-edit-${v.id}`}>edit</button>
                  {deleteOpen && <button className="text-xs text-red-600 underline" onClick={() => remove(v.id)}
                          data-testid={`venue-remove-${v.id}`}>delete</button>}
                </td>
              </tr>
            ))}
            {!loading && filtered.length === 0 && (
              <tr><td colSpan={6} className="p-8 text-center text-neutral-500">no venues match yet — try the backfill.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {mergeState && (
        <div role="dialog" aria-modal
             className="fixed inset-0 z-[70] grid place-items-center bg-black/70 p-4"
             onClick={closeMerge}
             data-testid="venue-merge-overlay">
          <div className="max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-xl bg-white p-6 shadow-xl"
               onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between">
              <p className="label-tiny">merge venues · pick the keeper</p>
              <button onClick={closeMerge} className="text-xs underline">close</button>
            </div>
            <h2 className="mt-1 font-display text-xl font-bold">every event, review, photo and follow will move to the keeper.</h2>
            <ul className="mt-4 space-y-2" data-testid="venue-merge-choices">
              {(mergeState.rows || []).map((v) => (
                <li key={v.id}
                    className={`flex items-center gap-2 rounded-md border p-2 ${mergeState.keep_id === v.id ? "border-amber-500 bg-amber-50" : "border-neutral-200"}`}>
                  <input type="radio" name="keep" data-testid={`venue-keep-${v.id}`}
                         checked={mergeState.keep_id === v.id}
                         onChange={() => setMergeState((s) => ({ ...s, keep_id: v.id }))} />
                  <div className="flex-1">
                    <p className="font-semibold lowercase">{v.name}</p>
                    <p className="text-xs text-neutral-500">
                      {v.area || "—"} · {(v.event_count_upcoming || 0) + (v.event_count_past || 0)} events{v.aliases?.length ? ` · ${v.aliases.length} alias` : ""}
                    </p>
                  </div>
                </li>
              ))}
            </ul>
            <div className="mt-6 flex justify-end gap-2">
              <button onClick={runMerge} data-testid="venue-merge-run"
                      className="rounded-md bg-amber-600 px-4 py-2 text-sm font-semibold text-white">
                merge into keeper
              </button>
            </div>
          </div>
        </div>
      )}
      {del && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" data-testid="venue-delete-dialog">
          <div className="w-full max-w-md rounded-xl bg-white p-6">
            <p className="mb-2 text-lg font-bold lowercase">delete {(del.venue?.name || "this venue").toLowerCase()}?</p>
            {del.counts.total === 0 ? (
              <p className="text-sm text-neutral-700" data-testid="venue-delete-no-events">no events point at this venue. better to merge if it's a duplicate.</p>
            ) : (
              <>
                <p className="mb-3 rounded-md bg-amber-50 p-3 text-sm text-amber-800" data-testid="venue-delete-counts">
                  {del.counts.published} published, {del.counts.pending} pending still point at this venue. pick where they move before deleting:
                </p>
                <select data-testid="venue-delete-move-to" value={del.moveTo}
                        onChange={(e) => setDel((d) => ({ ...d, moveTo: e.target.value }))}
                        className="w-full rounded-md border border-neutral-300 p-2 text-sm">
                  <option value="">pick a venue…</option>
                  {rows.filter((r) => r.id !== del.venue?.id).map((r) => (
                    <option key={r.id} value={r.id}>{(r.name || "").toLowerCase()}{r.area ? ` · ${r.area.toLowerCase()}` : ""}</option>
                  ))}
                </select>
              </>
            )}
            <div className="mt-4 flex justify-end gap-2">
              <button className="rounded-md border border-neutral-300 px-3 py-2 text-sm" data-testid="venue-delete-cancel" onClick={() => setDel(null)}>cancel</button>
              <button className="rounded-md bg-red-600 px-4 py-2 text-sm font-semibold text-white disabled:opacity-50" data-testid="venue-delete-confirm" onClick={confirmRemove}
                      disabled={del.counts.total > 0 && !del.moveTo}>
                {del.counts.total > 0 ? "move events and delete" : "delete"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
